import { createComposer, type ComposeOptions } from '@signa/renderer';
import {
  BufferTarget,
  CanvasSource,
  canEncodeVideo,
  Mp4OutputFormat,
  Output,
  Quality,
} from 'mediabunny';
import {
  advanceHiddenMark,
  bitrateFor,
  captureAction,
  exportDuration,
  isTruncated,
  progressOf,
  startStallWatch,
  watchStall,
  EXPORT_FPS,
  type Size,
} from './encode-plan.js';

/**
 * 合成結果を MP4 に書き出す(#181 / [ADR-0036](../../../../docs/adr/0036-video-export-webcodecs.md))。
 *
 * ## 方式
 *
 * **WebCodecs で符号化し、[mediabunny](https://mediabunny.dev/) の出力機能で MP4 に固める**
 * (決定 1・2)。**採る理由は「出力形式がどこでも MP4 / H.264 で揃う」こと** ——
 * `MediaRecorder` は Chrome で WebM、Safari で MP4 に分かれる。**人に渡すファイル**なので、
 * どこで作っても同じ形式である必要がある。
 *
 * ## 素材はシークせず、再生しながら撮る
 *
 * **1/30 秒ずつシークする方式は捨てた**(#181 の実測)。
 * **シークのたびにブラウザがデコードをやり直し、1 フレームに 665ms かかっていた** ——
 * 30 秒の素材で 4 分待つことになる。内訳は次のとおりで、**合成は問題ではなかった:**
 *
 * | 処理 | 実測 |
 * |---|---|
 * | 合成(背景 + WebGL + 転写 + マスク) | **1.65ms** |
 * | シーク + デコード + 符号化 | **約 663ms** |
 *
 * **再生しながら撮れば実時間で進む。** 合成が 1.65ms なので、30fps の予算(33ms)に十分収まる。
 * **実時間より速くはできない**が、シークより 20 倍速い。
 *
 * ## ネットワークを使わない
 *
 * **このモジュールは `fetch` / `XHR` / `WebSocket` を持たない。** 設計書 9.5 の
 * 「素材を永続化しない」は**送信のコードを持たないこと**で担保しており、
 * **生成物も同じ**である —— 書き出した MP4 もサーバへは出さない。
 */

/** 書き出しの失敗の理由。**黙って壊れない**ため、呼び出し側が文言を選べるようにする。 */
export type ExportFailure =
  /** この環境では符号化できない(WebCodecs 非対応・コーデック非対応)。 */
  | 'unsupported'
  /** 利用者が中止した。 */
  | 'aborted'
  /** 合成や符号化の途中で落ちた。 */
  | 'failed';

export type ExportResult =
  | {
      readonly ok: true;
      readonly blob: Blob;
      /** **上限で切ったか**(ADR-0036 決定 6)。切ったときは利用者に伝える。 */
      readonly truncated: boolean;
    }
  | { readonly ok: false; readonly reason: ExportFailure };

export interface ExportVideoOptions extends ComposeOptions {
  /**
   * 書き出しの対象になる動画素材。
   *
   * **長さの決定とシークに使う。** 呼び出し側が
   * [`playableOf`](./playback.ts) で取り出したものをそのまま渡す
   * (**静止画を除き、同じ要素は 1 度だけ**)。
   */
  readonly videos: readonly HTMLVideoElement[];
  /** 進捗(0〜1)。 */
  readonly onProgress?: (ratio: number) => void;
  /** 中止の合図。 */
  readonly signal?: AbortSignal;
}

/**
 * この環境で書き出せるか。**実行時に確かめる**(ADR-0036 決定 8)。
 *
 * **静的なブラウザ判定はしない。** H.264 の符号化は**端末のハードウェア次第**で、
 * ブラウザのバージョンだけでは決まらない。
 *
 * ## 自前でコーデック文字列を組まない
 *
 * **`avc1.42001f`(Baseline Level 3.1)を直に渡して踏み抜いた** ——
 * **Level 3.1 の上限は 1280×720** なので、**1080p の盤面で必ず「非対応」になる。**
 * プロファイルと Level は**寸法とフレームレートから決まる**ので、手で書くと寸法ごとに間違える。
 *
 * **`canEncodeVideo` は、実際に符号化するときと同じ組み立てで判定する** ——
 * 書き出す側([`CanvasSource`](https://mediabunny.dev/))と判定がずれない。
 *
 * ## 渡された寸法のまま確かめる(#356)
 *
 * **以前はここで寸法を偶数に丸めていた。** 符号化には丸める前の奇数の寸法が渡っていたので、
 * **ここは「書き出せる」と答え、符号化は最初の 1 枚で例外になっていた**(H.264 は偶数しか受け付けない)。
 * 偶数にするのは合成器の側([`createComposer`](../../../renderer/src/compose.ts) の `even`)で、
 * **ここには実際に符号化する canvas の寸法を渡す。** 奇数を渡せば、正しく「書き出せない」と答える。
 */
export async function canExportVideo(size: Size): Promise<boolean> {
  try {
    return await canEncodeVideo('avc', {
      width: size.width,
      height: size.height,
      quality: new Quality({ bitrate: bitrateFor(size) }),
      // 符号化は `addVideoTrack(…, { frameRate: EXPORT_FPS })` で組む。**確認も同じ値で**(#356 の最終レビュー)。
      frameRate: EXPORT_FPS,
    });
  } catch {
    // 設定が不正だと投げる実装がある。**投げたら「使えない」と見なす。**
    return false;
  }
}

/**
 * いまの状態を MP4 に書き出す。
 *
 * **長さは最も長い素材 1 周、上限 60 秒**(ADR-0036 決定 6)。
 * 素材はループ再生なので 1 周すれば全部の見え方が出る。
 */
export async function exportVideo(options: ExportVideoOptions): Promise<ExportResult> {
  /*
   * **偶数の寸法で合成する**(#356)。盤面の寸法は背景写真の縮小後の実寸で、奇数になりうる ——
   * H.264 は偶数しか受け付けず、**奇数のままだと最初の 1 枚で例外になっていた。**
   */
  const composer = createComposer(options, { even: true });
  if (composer === null) {
    warnExportFailure('合成の準備ができませんでした(WebGL2 が使えない、または盤面の寸法が不正)');
    return { ok: false, reason: 'unsupported' };
  }

  // **符号化する canvas の寸法そのもの**で確かめる —— 確認と符号化で寸法がずれない(#356)。
  const size = { width: composer.canvas.width, height: composer.canvas.height };
  if (!(await canExportVideo(size))) {
    warnExportFailure(
      `この環境では H.264(${String(size.width)}×${String(size.height)})を符号化できません`,
    );
    composer.dispose();
    return { ok: false, reason: 'unsupported' };
  }

  const durations = options.videos.map((v) => v.duration);
  const duration = exportDuration(durations);
  const truncated = isTruncated(durations);

  const output = new Output({ format: new Mp4OutputFormat(), target: new BufferTarget() });
  const source = new CanvasSource(composer.canvas, {
    codec: 'avc',
    quality: new Quality({ bitrate: bitrateFor(size) }),
  });
  output.addVideoTrack(source, { frameRate: EXPORT_FPS });

  /*
   * **止めたら中間データを残さない**(受け入れ条件)。
   *
   * `cancel()` はエンコーダと出力先を捨てる。呼ばないと、**中止したつもりで
   * バッファが残り続ける。**
   */
  /** 書き出しの前の再生状態。**終わったら戻す** —— 画面の再生を壊さない。 */
  const before = options.videos.map(snapshotOf);

  const abandon = async (): Promise<void> => {
    restoreAll(options.videos, before);
    composer.dispose();
    try {
      await output.cancel();
    } catch {
      // 既に終わっている・始まっていない場合は投げる。**捨てるだけなので無視してよい。**
    }
  };

  try {
    /*
     * **描き始める前に、素材にフレームを持たせる**(#181 で踏んだ)。
     *
     * **一度も再生・シークしていない `<video>` は、`readyState` が 4(HAVE_ENOUGH_DATA)でも
     * テクスチャへ転送できないことがある** —— `texImage2D` が失敗し、**盤面が黒いまま**になる。
     *
     * 書き出しの 1 枚目は時刻 0 なので、`currentTime` が既に 0 だと
     * **シークがスキップされ、フレームの無い素材を描いてしまう。**
     * ここで一度動かしておけば、以降は普通にシークできる。
     */
    await primeVideos(options.videos);

    await output.start();

    const interval = 1 / EXPORT_FPS;

    if (duration <= 0) {
      /*
       * **動く素材が無い**(静止画だけ / 長さが取れない)。
       * **1 枚だけ出す** —— 0 枚だと中身の無い MP4 になり、利用者には壊れて見える。
       */
      composer.draw();
      await source.add(0, interval);
      options.onProgress?.(1);
    } else {
      /*
       * **基準にする素材**(#235)。**最も長いもの。**
       *
       * 書き出しの長さ([`exportDuration`](./encode-plan.ts))はこの素材で決まっているので、
       * **この素材が終端に着いたら撮り終えたことになる**(上限 60 秒で切る場合は、切った時点で終わる)。
       * **この素材だけループさせない**([`startCapture`](#startCapture) —— #354)。
       */
      const reference = longestOf(options.videos);

      /*
       * **素材を先頭から流し、実時間で撮る。**
       *
       * **全部を同時に開始する**ので、画面で見ていた「同期」した状態と同じ並びになる
       * (#123 と同じ論点)。**基準以外をループさせる**のは、素材が書き出しより短いとき
       * 表示と同じく繰り返すため(設計書 423)。
       */
      await startCapture(options.videos, reference);

      /*
       * **間に合わないときは素材を遅く再生する**(#181 / 利用者の要件)。
       *
       * **等倍で再生しながら撮ると、処理が重い環境ではフレームが落ちる** ——
       * 実機で **30fps のはずが 14.4fps** になった(30 秒の出力に 432 枚)。
       * 落ちた絵は戻らないので、**書き出しがカクつく。**
       *
       * **素材を遅く流せば、1 枚あたりに使える時間が増える。**
       * 出力のタイムスタンプは**素材上の時刻**なので、
       * **できあがる動画の長さも滑らかさも変わらない** —— 待ち時間だけが延びる。
       *
       * **下限は実時間の 2.5 倍**(`MIN_RATE`)。利用者の要件が
       * 「動画時間の 2〜2.5 倍まで」なので、そこで止める ——
       * それ以上遅くすると、**なめらかさのために待たせすぎる。**
       */
      let rate = 1;
      /** 次に撮りたい時刻(秒)。**30fps の等間隔**を目標にする。 */
      let nextStamp = 0;
      /** 直前に撮った時刻(秒)。**タイムスタンプを単調増加に保つ**ために持つ。 */
      let lastStamp = -1;

      /*
       * **基準の素材が進んでいるかを見張る**(#354)。
       *
       * 自動再生を拒まれた・デコードが止まった素材は進まず、**撮る時刻に永遠に届かない** ——
       * 書き出しが 0% のまま止まり続けていた。進まなければ失敗として止める。
       *
       * **隠れていた時間は数えない。** 隠れている間は rAF が止まってこのループも止まるので、
       * 戻った最初の 1 回に隠れていた時間がまとめて乗る。`visibilitychange` で
       * 「前回から隠れていた」を拾い、そのときは数え直す。
       *
       * ## 隠れている間は素材も止める(#354)
       *
       * **ループは止まっても、素材の時計は進みうる** —— 音声トラックを持つ素材はミュートでも
       * 背景で再生を続けることがある。基準はループさせていないので、**隠れている間に終端まで進むと、
       * 戻った最初の 1 回で「撮り終えた」になり、後半の欠けた動画を成功として渡してしまう。**
       * 止めておけば戻ったところから続きを撮れる(出力の時刻は素材の位置なので、継ぎ目も出ない)。
       */
      let stall = startStallWatch(reference === null ? duration : reference.currentTime);
      let lastCheck = performance.now();
      let hiddenSinceCheck = document.visibilityState === 'hidden';
      const hold = holdWhileHidden(options.videos);
      const onVisibilityChange = (): void => {
        if (document.visibilityState === 'hidden') {
          hiddenSinceCheck = true;
          hold.hide();
        } else {
          hold.show();
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      // **始める前に隠れていた**(シークや再生の開始を待つ間に切り替えた)。いまから止める。
      if (document.visibilityState === 'hidden') hold.hide();

      try {
        for (;;) {
          if (options.signal?.aborted === true) {
            await abandon();
            return { ok: false, reason: 'aborted' };
          }

          /*
           * **素材の実際の位置を見る**(#235 で踏んだ)。
           *
           * **以前は「実経過 × 再生速度」で計算していた** —— 処理が遅れても
           * 素材とずれないようにするためだったが、**逆の事故を見落としていた。**
           *
           * **4K のデコードが追いつかないと、ブラウザはフレームを飛ばして時刻だけを進める。**
           * `playbackRate` を下げても実際の再生は先へ行くので、
           * **計算値が 30 秒を数える間に素材は 60 秒以上進み、倍速・2 ループ分が書き出された。**
           *
           * 素材の位置を基準にすれば、**飛んでも出力の長さは正しくなる**
           * (そのぶんフレームレートは落ちる —— 長さが狂うよりはよい)。
           */
          const mediaTime = reference === null ? duration : reference.currentTime;

          const now = performance.now();
          // **まだ隠れているなら印を残す**([`advanceHiddenMark`](./encode-plan.ts))。
          const mark = advanceHiddenMark(hiddenSinceCheck, document.visibilityState === 'hidden');
          stall = watchStall(stall, mediaTime, now - lastCheck, mark.wasHidden);
          lastCheck = now;
          hiddenSinceCheck = mark.next;

          /*
           * **終端は `ended` か位置で見る**(#354)。基準はループさせていないので、
           * 終端に着けば位置が止まる —— **読む間隔によらず取りこぼさない。**
           */
          const action = captureAction({
            mediaTime,
            ended: reference?.ended ?? false,
            duration,
            nextStamp,
            stalledMs: stall.stalledMs,
          });
          if (action === 'done') break;
          if (action === 'stalled') {
            warnExportFailure('素材の再生が止まったまま進みませんでした');
            await abandon();
            return { ok: false, reason: 'failed' };
          }

          // **撮る時刻まで素材が進むのを待つ。**
          if (action === 'wait') {
            await nextTick();
            continue;
          }

          const begin = performance.now();
          composer.draw();
          /*
           * **時刻は素材の位置。** ただし**単調増加を保つ** ——
           * `add` は時刻が戻ると受け付けない。
           *
           * **`add` は待つ。** 返る Promise が**バックプレッシャー**になっていて、
           * 待たずに回すとエンコーダのキューに積み上がる。
           */
          const timestamp = lastStamp < 0 ? 0 : Math.max(mediaTime, lastStamp + interval / 2);
          await source.add(timestamp, interval);
          lastStamp = timestamp;
          const cost = (performance.now() - begin) / 1000;

          /*
           * **素材が飛んだ分は追いかける。**
           *
           * 等間隔で進めるだけだと、**飛んだ区間を埋めようとして同じ絵を撮り続ける** ——
           * 無駄なフレームで時間を使い、いつまでも追いつかない。
           */
          nextStamp = Math.max(nextStamp + interval, mediaTime);

          /*
           * **1 枚の処理が予算に食い込んでいたら減速する。**
           *
           * 予算は 1/30 秒。**8 割を超えたら**次から遅くする ——
           * ぎりぎりまで待つと、ばらつきでフレームを落とす。
           */
          if (cost > interval * SLOW_THRESHOLD && rate > MIN_RATE) {
            rate = Math.max(MIN_RATE, rate * RATE_STEP);
            applyRate(options.videos, rate);
          }

          options.onProgress?.(progressOf(mediaTime, duration));
          await nextTick();
        }
      } finally {
        // **どの出口でも外す**(撮り終えた・中止・止まった・例外)。
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }

      restoreAll(options.videos, before);
    }

    await output.finalize();
    const buffer = output.target.buffer;
    if (buffer === null) {
      warnExportFailure('出力が空でした');
      composer.dispose();
      return { ok: false, reason: 'failed' };
    }

    restoreAll(options.videos, before);
    composer.dispose();
    return { ok: true, blob: new Blob([buffer], { type: 'video/mp4' }), truncated };
  } catch (error) {
    // **後片付けを先に済ませる** —— 警告の処理で何が起きても、WebGL と出力先は必ず捨てる。
    await abandon();
    // **理由を残す**(#356)。以前はここで握りつぶしていて、奇数の寸法の例外に気付けなかった。
    warnExportFailure('合成か符号化の途中で失敗しました', error);
    return { ok: false, reason: 'failed' };
  }
}

/**
 * 書き出しに失敗した理由をコンソールに出す(#356 / 同意書 Q1)。
 *
 * **画面の文言(「書き出せませんでした」など)は変えない** —— 閲覧者に技術的な理由を見せても
 * 直せない。**埋め込み先の開発者と、調べる人のために残す**(`INVALID_TOKEN` や 403 と同じ方針 ——
 * [`api.ts`](./api.ts))。以前は理由がどこにも残らず、**奇数の寸法で失敗していることに気付けなかった。**
 *
 * **こちらから背景画像の URL は足さない**(公開トークンを含む)。例外があれば、その文言だけを添える
 * (起きうる例外 —— mediabunny・WebGL・`toBlob` —— の文言に URL は入らない)。
 * **中止は失敗ではないので呼ばない。** **このために投げない**(呼び出し側の後片付けを妨げない)。
 */
export function warnExportFailure(reason: string, error?: unknown): void {
  const detail = error === undefined ? '' : `: ${messageOf(error)}`;
  console.warn(`[SignaPreview] 書き出せませんでした。${reason}${detail}`);
}

/**
 * 例外の文言。**`message` を持たない値は文字列にする。** 別レルムの例外もあるので `instanceof` は使わない。
 *
 * **文字列にできない値でも投げない**(プロトタイプの無いオブジェクトや、`toString` が投げる値)。
 */
function messageOf(error: unknown): string {
  try {
    if (typeof error === 'object' && error !== null && 'message' in error) {
      const { message } = error;
      if (typeof message === 'string') return message;
    }
    return String(error);
  } catch {
    return '(理由を文字にできない例外)';
  }
}

/**
 * 素材を時刻 `time` に合わせる。
 *
 * **素材より書き出しが長ければ巻き戻して繰り返す** —— 表示はループ再生
 * (設計書 423)なので、書き出しでも同じ見え方にする。
 *
 * **`seeked` を待たずに描くと前のフレームが写る。** ただし
 * **待ち続けてもいけない** —— 壊れた素材では `seeked` が来ないことがあり、
 * **書き出しがそこで止まる。** 上限を置いて、来なければそのまま進む
 * (**1 フレームずれるより、終わらないほうが痛い**)。
 */
function seekTo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve) => {
    // メタデータが無い(`readyState < 1`)ものはシークできない。そのまま描く。
    if (video.readyState < 1 || !Number.isFinite(video.duration) || video.duration <= 0) {
      resolve();
      return;
    }

    const target = time % video.duration;
    // 既にその位置なら待たない(**同じ位置へのシークは `seeked` が来ない**)。
    if (Math.abs(video.currentTime - target) < 1 / (EXPORT_FPS * 4)) {
      resolve();
      return;
    }

    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      video.removeEventListener('seeked', finish);
      resolve();
    };

    const timer = setTimeout(finish, SEEK_TIMEOUT_MS);
    video.addEventListener('seeked', finish, { once: true });
    video.currentTime = target;
  });
}

/** シークの待ちの上限(ms)。**来なければ諦めて進む。** */
const SEEK_TIMEOUT_MS = 2000;

/**
 * 素材の再生速度の下限(#181)。
 *
 * **実時間の 2.5 倍**まで遅くしてよい —— 利用者の要件が
 * 「書き出しは動画時間の 2〜2.5 倍まで」なので、そこで止める。
 */
const MIN_RATE = 0.4;

/** 1 枚の処理がこの割合を超えたら減速する(予算 = 1/30 秒)。 */
const SLOW_THRESHOLD = 0.8;

/** 減速の刻み。**一度に大きく落とさない** —— 速い環境で無駄に待たせない。 */
const RATE_STEP = 0.9;

/**
 * 最も長い素材を返す(#235)。**書き出しの基準になる。**
 *
 * 長さの取れないもの(`NaN` / `Infinity`)は数えない。
 * **1 本も無ければ `null`** —— 呼び出し側は「もう撮り終えた」として扱う。
 */
function longestOf(videos: readonly HTMLVideoElement[]): HTMLVideoElement | null {
  let best: HTMLVideoElement | null = null;
  for (const video of videos) {
    if (!Number.isFinite(video.duration) || video.duration <= 0) continue;
    if (best === null || video.duration > best.duration) best = video;
  }
  return best;
}

/** 素材の再生速度をそろえる。 */
function applyRate(videos: readonly HTMLVideoElement[], rate: number): void {
  for (const video of videos) {
    try {
      video.playbackRate = rate;
    } catch {
      // 受け付けない実装がある。**等倍のまま続ける**(落ちるよりよい)。
    }
  }
}

/**
 * 素材にフレームを持たせる(#181)。
 *
 * **`<video>` を一度も動かさないと、テクスチャへ転送できないことがある。**
 * `readyState` が 4(HAVE_ENOUGH_DATA)でも起きる ——
 * **書き出しも静止画保存も丸ごと黒くなる原因**だった。
 *
 * **いまと違う位置へ動かしてから、元の位置へ戻す。** 同じ位置への代入では
 * `seeked` が来ないので「動かした」ことにならない。
 * **元へ戻すのは、静止画保存が「いま見えている位置」を保存するため** ——
 * 勝手に頭出しすると、画面と保存した絵が食い違う。
 *
 * **表示(ビューアの通常再生)では起きない。** あちらは毎フレーム描き直すので、
 * 1 回目に失敗しても次で戻る。**1 回しか描かない経路だけが踏む。**
 */
export async function primeVideos(videos: readonly HTMLVideoElement[]): Promise<void> {
  await Promise.all(videos.map((video) => primeVideo(video)));
}

async function primeVideo(video: HTMLVideoElement): Promise<void> {
  if (video.readyState < 1 || !Number.isFinite(video.duration) || video.duration <= 0) return;

  const before = video.currentTime;
  const away = before < video.duration / 2 ? video.duration / 2 : 0;
  await seekTo(video, away);
  await seekTo(video, before);
}

// ---------------------------------------------------------------------------
// 再生しながら撮る(#181)
// ---------------------------------------------------------------------------

/** 書き出しの前の再生状態。**終わったら戻す。** */
interface PlaybackSnapshot {
  readonly currentTime: number;
  readonly paused: boolean;
  readonly loop: boolean;
  /**
   * 再生速度(#235)。
   *
   * **戻し忘れると次の書き出しに持ち越される** —— 書き出しは処理が重いと
   * `playbackRate` を下げる。戻さないと**2 回目が遅い素材で始まり**、
   * 出力の長さが狂った(倍速・複数ループ)。
   */
  readonly playbackRate: number;
}

function snapshotOf(video: HTMLVideoElement): PlaybackSnapshot {
  return {
    currentTime: video.currentTime,
    paused: video.paused,
    loop: video.loop,
    playbackRate: video.playbackRate,
  };
}

/**
 * 書き出しのために素材を先頭から流す。
 *
 * **基準以外はループさせる** —— 素材が書き出しより短いとき、表示と同じく繰り返す(設計書 423)。
 * **ミュートにする** —— 音は使わないうえ、**音があると自動再生が拒否される。**
 *
 * ## 基準(`reference`)だけはループさせない(#354)
 *
 * **書き出しは基準が終端に着いたら終わる。** ループさせると基準は終端で 0 に戻り、
 * **位置が `duration` に見えるのは切れ目の一瞬だけ**になる —— 読み逃すと、
 * **99% のまま素材をもう 1 周撮り続けていた**(#354 の実測で毎回 1 周ぶん余計にかかった)。
 * ループさせなければ終端で位置が止まり、`ended` も立つ。
 *
 * **書き出しの後で戻す**([`restoreAll`](#restoreAll))。終端で止まった基準は、
 * 書き出しの前に流れていれば流し直す。
 *
 * ## そろえてから流す(#245)
 *
 * **シークの所要時間は素材ごとに違う。** [`seekTo`](#seekTo) は `seeked` を待ち(上限 2 秒)、
 * 待ち時間は**解像度・コーデック・デコーダの状態**で変わる。
 *
 * **素材ごとに「シーク → 再生」を直列で回すと、終わった素材から順に流れ始める** ——
 * 4K と FHD が混ざると開きやすく、**250ms ずれれば 30fps で約 7 フレーム**が
 * そのまま出力に入る。書き出しのループは最長素材の位置を基準に撮る(#235)ので
 * **長さは正しいが、素材どうしの相対位置がずれる。**
 *
 * **だから 2 段に分ける。** 全部のシークが終わってから、まとめて再生を始める。
 * **開始は最も遅いシークに合わせて最大 2 秒待つ**が、書き出し自体が数十秒かかるので
 * 誤差の範囲(#245 の同意書 Q1)—— **タイムアウトを縮めると、間に合わなかった素材が
 * ずれたまま残って本末転倒。**
 *
 * **テストのために `export` している。** 検査できるのは**手順の順序**まで ——
 * 符号化そのものは jsdom で動かない([ADR-0019](../../../../docs/adr/0019-dev-foundation.md))。
 */
export async function startCapture(
  videos: readonly HTMLVideoElement[],
  /** 書き出しの長さを決めた素材。**これだけループさせない。** 無ければ `null`。 */
  reference: HTMLVideoElement | null,
): Promise<void> {
  /*
   * **1 段目: そろえる。** 止めて、初期設定して、シークを全部終える。
   */
  await Promise.all(
    videos.map((video) => {
      video.loop = video !== reference;
      video.muted = true;
      /*
       * **必ず等倍から始める**(#235)。
       *
       * 前の書き出しで落とした速度が残っていると、**減速の判断が積み上がって**
       * 想定より遅い素材で撮ることになる。
       */
      applyRate([video], 1);
      /*
       * **シークの前に止める**(#245)。**ここを外すとそろわない。**
       *
       * `currentTime` への代入は**再生を止めない。** 流れたままシークすると、
       * **速く終わった素材は 0 から進み始め、遅い素材のシークを待つ間に先行する** ——
       * 2 段目で `play()` を並べても、**既に流れている素材には効かない**(no-op)ので
       * ずれが残る。ビューアは既定で自動再生し、**書き出し中も素材の再生は続く**
       * (`shell.ts` の `beginExportUi` が止めるのは表示の描画だけ)。
       */
      video.pause();
      return seekTo(video, 0);
    }),
  );

  /*
   * **2 段目: まとめて流す。**
   *
   * `map` で `play()` の**呼び出しを連続させてから**待つ。
   * `for (const video of videos) await video.play()` と書くと**再び順番待ち**になり、
   * 直す前と変わらない —— **ここが #245 の要点。**
   */
  await Promise.all(
    videos.map((video) =>
      withinTimeout(
        video.play().catch(() => {
          /*
           * **再生できなくても続ける。** 自動再生を拒まれても、
           * **描けるフレームはある**(prime 済み)—— 止まった絵で書き出すほうが、
           * 何も出さないよりよい。基準の素材が進まなければ、書き出しのループが
           * 10 秒で失敗として止める(#354)。
           */
        }),
        PLAY_TIMEOUT_MS,
      ),
    ),
  );
}

/**
 * 再生の開始の待ちの上限(ms)(#354)。**来なければ諦めて進む。**
 *
 * **`play()` の約束は決着しないことがある** —— 復号に失敗した素材(`MEDIA_ERR_DECODE`)で起きる
 * ([`@signa/renderer` の `tryPlay`](../../../renderer/src/source.ts))。待ち続けると
 * **書き出しが 0% のまま止まり、中止も効かない**(中止はループの中で見ている)。
 * 進んだ先で、基準の素材が進まなければループが失敗として止める。
 *
 * **シークの待ち([`SEEK_TIMEOUT_MS`](#SEEK_TIMEOUT_MS))と同じ長さ** —— 開始の足並み(#245)は
 * 最も遅い素材に合わせて待つので、ここを縮めると間に合わなかった素材がずれたまま残る。
 */
const PLAY_TIMEOUT_MS = 2000;

/** `promise` を待つ。**`ms` を過ぎたら待たずに進む**(結果は成否とも捨てる)。 */
function withinTimeout(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    const done = (): void => {
      clearTimeout(timer);
      resolve();
    };
    // **拒否も捌く。** 捌かないと unhandled rejection になり、2 秒待つことにもなる。
    promise.then(done, done);
  });
}

/** 隠れている間の素材の止め方。[`holdWhileHidden`](#holdWhileHidden) が返す。 */
export interface HiddenHold {
  /** 流れている素材を止めて覚える。 */
  hide(): void;
  /** 覚えた素材を流し直す。 */
  show(): void;
}

/**
 * 書き出しの間、**ページが隠れている間は素材を止める**(#354)。
 *
 * 隠れている間は rAF が止まり、撮れない。**素材の時計だけが進むと、** 戻ったときに
 * 基準が終端を過ぎていて**後半の欠けた動画が成功として出る**(基準はループさせていない)。
 *
 * **流し直すのは自分が止めた素材だけ。** 元から止まっていた素材(自動再生を拒まれた・
 * 終端に着いた基準)を流すと、**終端の基準が 0 から再生し直し、もう 1 周撮る**ことになる。
 *
 * **テストのために `export` している。**
 */
export function holdWhileHidden(videos: readonly HTMLVideoElement[]): HiddenHold {
  const held = new Set<HTMLVideoElement>();
  return {
    hide(): void {
      for (const video of videos) {
        /*
         * **終端に着いた素材は覚えない。** 終端では `ended` が先に立ち、`paused` は後の
         * タスクで立つ —— その間に隠れると「流れている」に見える。覚えて流し直すと
         * **0 から再生し直し、もう 1 周撮る**(#354 の再レビュー)。
         */
        if (video.paused || video.ended) continue;
        video.pause();
        held.add(video);
      }
    },
    show(): void {
      const resume = [...held];
      held.clear();
      for (const video of resume) {
        void video.play().catch(() => {
          // 拒まれたら止まったまま。基準なら書き出しのループが 10 秒で失敗として止める。
        });
      }
    },
  };
}

/** 再生状態を書き出しの前に戻す。**画面の再生を壊さない。** */
function restoreAll(
  videos: readonly HTMLVideoElement[],
  snapshots: readonly PlaybackSnapshot[],
): void {
  videos.forEach((video, index) => {
    const snapshot = snapshots[index];
    if (snapshot === undefined) return;
    video.loop = snapshot.loop;
    // **速度を必ず戻す**(#235)。残すと次の書き出しに持ち越される。
    applyRate([video], snapshot.playbackRate);
    if (snapshot.paused) video.pause();
    video.currentTime = snapshot.currentTime;
    /*
     * **流れていた素材は流し直す**(#354)。
     *
     * 基準の素材はループさせずに撮るので、**終端で止まったまま戻ってくる。**
     * 書き出しの前に流れていたなら、画面の再生もそのまま続いているべきである。
     * 拒まれても書き出しの結果には関係しないので、**黙って続ける。**
     */
    if (!snapshot.paused && video.paused) {
      void video.play().catch(() => {
        // 自動再生の拒否など。画面の「再生」で戻せる。
      });
    }
  });
}

/**
 * 次の描画の機会まで待つ。
 *
 * **`requestAnimationFrame` を使う** —— 画面の更新に合わせて進めることで、
 * **素材のデコードが自然に追いつく。** タイマーで詰めるとデコードを追い越し、
 * 同じ絵を何度も撮ることになる。
 */
function nextTick(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
}
